'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { ChevronRight, Clock, Mail, Phone, MapPin } from 'lucide-react';

export default function LegalPageLayout({
  title,
  subtitle,
  lastUpdated,
  sections,
}) {
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections
        .map((section) => document.getElementById(section.id))
        .filter(Boolean);

      const currentSection = sectionElements.find((element) => {
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className='min-h-screen from-white to-gray-50 dark:from-gray-900 dark:to-gray-950 pt-20'>
      {/* Hero Section */}
      <section className='relative bg-gradient-to-r from-[#38F5EF] to-[#FE2A53] py-20'>
        <div className='absolute inset-0 bg-black/20'></div>
        <div className='relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center text-white'>
            <h1 className='text-4xl md:text-5xl font-bold mb-4'>{title}</h1>
            <p className='text-xl md:text-2xl mb-6 opacity-90'>{subtitle}</p>
            <h2 className='sr-only'>X-Space Real Estate Broker | {title}</h2>
            <div className='flex items-center justify-center gap-2 text-sm opacity-80'>
              <Clock size={16} />
              <span>Last updated: {lastUpdated}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className='bg-white dark:bg-gray-950 border-b '>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4'>
          <nav className='flex items-center space-x-2 text-sm text-gray-600'>
            <Link
              rel='preload'
              href='/'
              className='hover:text-blue-600 text-primarycolor dark:text-secondarycolor transition-colors'
            >
              Home
            </Link>
            <ChevronRight size={16} className='dark:text-white' />
            <span className='text-black dark:text-white'>{title}</span>
          </nav>
        </div>
      </div>

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12'>
        <div className='grid grid-cols-1 lg:grid-cols-4 gap-8'>
          {/* Table of Contents */}
          <div className='lg:col-span-1'>
            <div className='sticky top-8'>
              <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
                <h3 className='text-lg font-semibold text-primarycolor dark:text-secondarycolor mb-4'>
                  Table of Contents
                </h3>
                <nav className='space-y-2'>
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        activeSection === section.id
                          ? 'bg-blue-100 text-blue-700 font-medium'
                          : 'text-black dark:text-white hover:bg-primarycolor hover:text-white'
                      }`}
                    >
                      <span className='mr-2'>{section.icon}</span>
                      {section.title}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Contact Information */}
              <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mt-6'>
                <h3 className='text-lg font-semibold text-primarycolor dark:text-secondarycolor mb-4'>
                  Contact Us
                </h3>
                <div className='space-y-3 text-sm'>
                  <div className='flex items-center gap-3'>
                    <Mail size={16} className='text-blue-600' />
                    <a
                      href='mailto:info@x-space.ae'
                      className='text-black dark:text-white hover:text-blue-600 dark:hover:text-blue-600'
                    >
                      info@x-space.ae
                    </a>
                  </div>
                  <div className='flex items-center gap-3'>
                    <Phone size={16} className='text-blue-600' />
                    <a
                      href='tel:+971-4-123-4567'
                      className='text-black dark:text-white hover:text-blue-600 dark:hover:text-blue-600'
                    >
                      +971 4 123 4567
                    </a>
                  </div>
                  <div className='flex items-start gap-3'>
                    <MapPin size={16} className='text-blue-600 mt-0.5' />
                    <span className='text-black dark:text-white'>
                      Dubai, United Arab Emirates
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className='lg:col-span-3'>
            <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md'>
              {/* Header */}
              <div className='p-8 border-b border-gray-200'>
                <div className='text-center'>
                  <h3 className='text-2xl font-bold text-primarycolor dark:text-secondarycolor mb-2'>
                    X-Space Real Estate Broker
                  </h3>
                  <p className='text-black dark:text-white'>
                    Effective Date: {lastUpdated}
                  </p>
                </div>
              </div>

              {/* Introduction */}
              <div className='p-8 border-b border-gray-200'>
                <p className='text-black dark:text-white leading-relaxed'>
                  At{' '}
                  <strong className='dark:text-white'>
                    X-Space Real Estate Broker
                  </strong>
                  , we are committed to maintaining the highest standards of
                  transparency and professionalism. This document outlines our
                  policies and your rights when using our services in Dubai,
                  United Arab Emirates.
                </p>
              </div>

              {/* Sections */}
              <div className='p-8'>
                <div className='space-y-12'>
                  {sections.map((section, index) => (
                    <section
                      key={section.id}
                      id={section.id}
                      className='scroll-mt-24'
                    >
                      <div className='flex items-center gap-3 mb-6'>
                        <span className='text-2xl'>{section.icon}</span>
                        <h3 className='text-xl font-semibold text-primarycolor dark:text-secondarycolor'>
                          {section.title}
                        </h3>
                      </div>
                      <div className='prose prose-gray max-w-none'>
                        {section.content}
                      </div>
                      {index < sections.length - 1 && (
                        <hr className='mt-8 border-gray-200' />
                      )}
                    </section>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className='p-8 bg-gray-50 rounded-b-lg'>
                <div className='text-center'>
                  <p className='text-gray-600 mb-4'>
                    If you have any questions about this {title.toLowerCase()},
                    please don't hesitate to contact us.
                  </p>
                  <div className='flex flex-col sm:flex-row gap-4 justify-center'>
                    <a
                      href='mailto:info@x-space.ae'
                      className='inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors'
                    >
                      <Mail size={16} className='mr-2' />
                      Email Us
                    </a>
                    <a
                      href='tel:+971-4-123-4567'
                      className='inline-flex items-center justify-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors'
                    >
                      <Phone size={16} className='mr-2' />
                      Call Us
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
